import { useEffect } from 'react'
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from './lib/queryClient'
import { Toaster, toast } from 'sonner'
import { consumeFlashToast } from './lib/flash'
import { Check, X as XIcon, AlertTriangle, Info } from 'lucide-react'
import { ProtectedRoute, FeatureProtectedRoute } from './routes'
import { RealtimeInvalidator } from './components/RealtimeInvalidator'
import { GlobalTooltip } from './components/GlobalTooltip'
import { PlatformTitle } from './components/PlatformTitle'
import { AdminLayout } from './components/layout/AdminLayout'
import { UserLayout } from './components/layout/UserLayout'
import { SignInPage } from './pages/auth/SignInPage'
import { SignUpPage } from './pages/auth/SignUpPage'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { ResetPasswordPage } from './pages/auth/ResetPasswordPage'
import { JoinPage } from './pages/auth/JoinPage'
import { NotFoundPage } from './pages/errors/NotFoundPage'
import { DashboardPage } from './pages/app/DashboardPage'
import { SubAdminDashboard } from './pages/app/SubAdminDashboard'
import { MembersPage } from './pages/app/MembersPage'
import { LoginActivityPage } from './pages/app/LoginActivityPage'
import { DepartmentsPage } from './pages/app/DepartmentsPage'
import { WorkRolesPage } from './pages/app/WorkRolesPage'
import { ExternalCollaboratorsPage } from './pages/app/ExternalCollaboratorsPage'
import { WorkspacesPage } from './pages/app/WorkspacesPage'
import { ProfilePage } from './pages/app/ProfilePage'
import { PreferencesPage } from './pages/app/PreferencesPage'
import { TaskMonitorPage } from './pages/app/TaskMonitorPage'
import { FilesPage } from './pages/app/FilesPage'
import { NotificationsPage } from './pages/NotificationsPage'
import { UserHomePage } from './pages/employee/HomePage'
import { RepliesPage } from './pages/employee/RepliesPage'
import { AssignedCommentsPage } from './pages/employee/AssignedCommentsPage'
import { SpacePage } from './pages/employee/SpacePage'
import { DocumentPage } from './pages/employee/DocumentPage'
import { TeamTasksPage } from './pages/employee/TeamTasksPage'
import { WorkspaceOverviewPage } from './pages/employee/WorkspaceOverviewPage'
import { TeamAnalyticsPage } from './pages/employee/TeamAnalyticsPage'
import { LeaderboardPage } from './pages/employee/LeaderboardPage'
import { ReportSettingsPage } from './pages/employee/ReportSettingsPage'
import {
  DailyReportPage,
  WeeklyReportPage,
  MonthlyReportPage,
  TeamReportsPage,
} from './pages/employee/ReportsPage'
import { WorkReportsPage } from './pages/app/WorkReportsPage'
import { ReportSchedulePage } from './pages/app/ReportSchedulePage'
import { UsageReportPage as CompanyUsageReportPage } from './pages/app/UsageReportPage'
import { GamificationSettingsPage } from './pages/app/GamificationSettingsPage'
import { IntegrationsPage } from './pages/app/IntegrationsPage'
import { TelegramPage } from './pages/app/TelegramPage'
import { ServexaSupportPage } from './pages/app/ServexaSupportPage'
import { ServexaFeaturesPage } from './pages/app/ServexaFeaturesPage'
import { BillingPage } from './pages/app/BillingPage'
import { ApiKeysPage } from './pages/app/ApiKeysPage'
import { AiUsagePage } from './pages/app/AiUsagePage'
import { PerformancePage } from './pages/app/PerformancePage'
import { WorkflowsPage } from './pages/app/WorkflowsPage'
import { SopsPage } from './pages/app/SopsPage'
import { AuditLogPage } from './pages/app/AuditLogPage'
import { AdminEmailLogsPage } from './pages/app/AdminEmailLogsPage'
// AchievementsPage was merged into PerformancePage on 2026-05-18 — the
// personal "trophy case" (badges + points + ledger) now renders inside
// the Performance page above the team analytics. /app/achievements
// stays reachable via a legacy redirect to /app/performance.
import { MyDayPage } from './pages/employee/MyDayPage'
import { GoalsPage } from './pages/employee/GoalsPage'
import { GoalDetailPage } from './pages/employee/GoalDetailPage'
import { OurProductsPage } from './pages/employee/OurProductsPage'
import { UserManualPage } from './pages/UserManualPage'
import { SearchPalette } from './components/SearchPalette'
import { BoardPage } from './pages/app/BoardPage'
import { CalendarPage } from './pages/app/CalendarPage'
// PerformancePage is also reused on the User panel for Manager / Team
// Leader. The component is identical — the backend applies a department
// scope automatically when the caller isn't a Company Admin.
import { RoleAwareLayout } from './components/layout/RoleAwareLayout'
import { SuperAdminLayout } from './components/layout/SuperAdminLayout'
import { ReferralsPage } from './pages/app/ReferralsPage'
import { PlatformReferralsPage } from './pages/platform/ReferralsPage'
import { ImpersonationBanner } from './components/ImpersonationBanner'
import { VisitorTracker } from './components/VisitorTracker'
import { PagePerfTracker } from './components/PagePerfTracker'
import { SuperAdminDashboard } from './pages/platform/SuperAdminDashboard'
import AnalyticsPage from './pages/app/AnalyticsPage'
import { TenantsPage } from './pages/platform/TenantsPage'
import { TenantDetailPage } from './pages/platform/TenantDetailPage'
import { AllUsersPage } from './pages/platform/AllUsersPage'
import { PlansPage } from './pages/platform/PlansPage'
import { CouponsPage } from './pages/platform/CouponsPage'
import { LeadsPage } from './pages/platform/LeadsPage'
import { SoftwareWatchPage } from './pages/platform/SoftwareWatchPage'
import { AnnouncementsPage } from './pages/platform/AnnouncementsPage'
import { AuditLogsPage } from './pages/platform/AuditLogsPage'
import { PlatformLoginActivityPage } from './pages/platform/PlatformLoginActivityPage'
import { VisitorLogsPage } from './pages/platform/VisitorLogsPage'
import { PagePerformancePage } from './pages/platform/PagePerformancePage'
import { SupportTicketsPage } from './pages/platform/SupportTicketsPage'
import { PaymentsPage } from './pages/platform/PaymentsPage'
import { SettingsPage as PlatformSettingsPage } from './pages/platform/SettingsPage'
import { PlatformAiUsagePage } from './pages/platform/PlatformAiUsagePage'
import { PlatformStoragePage } from './pages/platform/PlatformStoragePage'
import { SopIntegrationPage } from './pages/platform/SopIntegrationPage'
import { ServexaIntegrationPage } from './pages/platform/ServexaIntegrationPage'
import { TelegramIntegrationPage } from './pages/platform/TelegramIntegrationPage'
import { EmailTemplatesPage } from './pages/platform/EmailTemplatesPage'
import { EmailTemplateEditorPage } from './pages/platform/EmailTemplateEditorPage'
import { SentEmailsPage } from './pages/platform/SentEmailsPage'
import { useAuthStore } from './stores/authStore'
import {
  hydratePreferences,
  resetPreferencesSync,
  hasPendingPreferenceSync,
} from './lib/space-prefs'
import { resetDisplayPrefsForSignOut } from './lib/display-prefs'
import {
  scopeForUser,
  useMaintenanceStore,
} from './stores/maintenanceStore'
import { MaintenanceScreen } from './pages/MaintenanceScreen'
import { UsageReportPage } from './pages/platform/UsageReportPage'

// Moved to `lib/queryClient.js` so the auth store can clear it at every
// session boundary — see the note there. Constructing it here made it
// unreachable from non-React code, which is how a signed-out user's cache
// survived into the next sign-in.

// `landingFor` moved to `src/lib/landing.js` so this module exports
// only React components. Vite's Fast Refresh requires that — having
// a non-component named export here meant every save in App.jsx
// triggered a full page reload instead of an in-place HMR patch.
// Import it locally for internal use.
import { landingFor } from './lib/landing'

/**
 * Maintenance guard — sits inside the router so it can call
 * `useLocation` and `Navigate`. Redirects anyone whose panel scope is
 * down to `/maintenance`.
 *
 * WEBSITE maintenance takes down the whole public auth flow (sign-in /
 * forgot / reset / join) for normal visitors. The Master Admin gets in
 * via a bypass: land on `/sign-in?admin=1` and the auth-flow paths stay
 * reachable (the flag is remembered in sessionStorage so it survives the
 * query param being dropped mid-flow). Once signed in, `scopeForUser`
 * returns null for a Super Admin and the gate stops applying — they
 * reach `/super-admin/settings` to flip the toggles back off.
 */
// Public auth-flow paths taken down by WEBSITE maintenance — reachable
// only with the admin bypass. `/maintenance` and `/super-admin/*` are
// always allowed (handled in the gate) so the screen + recovery panel
// never get locked out.
const AUTH_FLOW_PATHS = new Set([
  '/sign-in',
  '/forgot-password',
  '/reset-password',
  '/join',
])
const MAINT_BYPASS_KEY = 'taskmagix.maint_bypass'

/** Record the Master Admin's WEBSITE-maintenance bypass when `?admin=1`
 *  is present (typically `/sign-in?admin=1`). Held in sessionStorage so it
 *  survives the query param being dropped mid-flow, and clears on tab close. */
function rememberMaintBypass(search) {
  try {
    if (new URLSearchParams(search).get('admin') === '1') {
      sessionStorage.setItem(MAINT_BYPASS_KEY, '1')
    }
  } catch {
    // sessionStorage can be unavailable in some privacy modes — the bypass
    // just won't persist, which fails safe (the visitor stays gated).
  }
}

/** Paths reachable even when the visitor's scope is in maintenance: the
 *  maintenance screen itself, the Super Admin recovery panel, and — only
 *  with the admin bypass — the public auth flow (sign-in / forgot / reset
 *  / join), which WEBSITE maintenance otherwise takes down. */
function isMaintenanceExemptPath(path) {
  if (path === '/maintenance') return true
  if (path.startsWith('/super-admin')) return true
  if (!AUTH_FLOW_PATHS.has(path)) return false
  try {
    return sessionStorage.getItem(MAINT_BYPASS_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * Plan-selection gate — applies after auth + maintenance, before the
 * routes mount. A Company Admin who landed via Google SSO had a
 * tenant bootstrapped with `planSelectedAt: null` (see Tenant.js).
 * Until they visit /billing and click one of the plan CTAs (Free
 * Trial / Starter / Growth / Gold), every other route redirects to
 * /billing.
 *
 * Why only Company Admin: Sub Admins / regular Users get invited by
 * the Company Admin AFTER a plan is selected — they don't exist as a
 * scenario while planSelectedAt is null. Super Admins live outside
 * tenants. Forcing the redirect on company_admin alone keeps the
 * gate surgical and avoids accidentally trapping invited members who
 * shouldn't even be in this state.
 *
 * Allowed paths while gated:
 *   • `/billing` — the destination itself
 *   • `/sign-in` / `/forgot-password` / `/reset-password` — auth flows
 *   • `/super-admin/*` — out-of-tenant routes (defence in depth; a
 *     company_admin shouldn't reach these anyway, but ProtectedRoute
 *     handles the role check independently)
 *
 * The gate releases on the next /auth/me poll after a plan is
 * activated — payments.service.js stamps `planSelectedAt` on every
 * paid and free activation.
 */
const PLAN_GATE_ALLOW_ALWAYS = new Set([
  '/billing',
  '/sign-in',
  '/forgot-password',
  '/reset-password',
])

function PlanGate({ children }) {
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const tenant = useAuthStore((s) => s.tenant)
  const authStatus = useAuthStore((s) => s.status)

  // Hold rendering until auth is settled. Without this, the first
  // paint after sign-in flashes the dashboard for a tick before the
  // store sees tenant.planSelectedAt and bounces to /billing.
  if (authStatus === 'unknown') return children

  // Only gate Company Admins of a tenant. Super Admins (no tenant)
  // and Sub Admins / Users (created after plan selection) skip.
  if (user?.role !== 'company_admin') return children
  if (!tenant) return children

  // Plan is already chosen — nothing to gate.
  if (tenant.planSelectedAt) return children

  const isAllowedPath =
    PLAN_GATE_ALLOW_ALWAYS.has(location.pathname) ||
    location.pathname.startsWith('/super-admin')
  if (!isAllowedPath) {
    return <Navigate to="/billing" replace />
  }
  return children
}

/**
 * Route guard for tenant-owner-only pages (Billing, API Keys, …). These are
 * hidden from the sidebar for Sub Admins via the `caOnly` rule in AdminLayout,
 * but the menu-hide alone doesn't stop a Sub Admin typing the URL. This bounces
 * any non-`company_admin` to their home so the client matches the server, which
 * is the real boundary — every billing/payment route is `requireRole('company_admin')`
 * (billing.routes.js / payments.routes.js), so a Sub Admin can never actually
 * purchase or move money regardless of this guard.
 */
function CompanyAdminRoute({ children }) {
  const role = useAuthStore((s) => s.user?.role)
  const authStatus = useAuthStore((s) => s.status)
  // Hold until auth settles so a real Company Admin isn't bounced on first paint.
  if (authStatus === 'unknown') return null
  if (role !== 'company_admin') return <Navigate to="/" replace />
  return children
}

/**
 * Dashboard dispatcher. Sub Admins render the grant-aware `SubAdminDashboard`,
 * which surfaces only the dashboard sections/data their assigned operations
 * allow; Company Admins render the full `DashboardPage`. Both stay on the one
 * `/dashboard` URL so deep links and `landingFor()` never branch.
 *
 * This scopes what the dashboard SHOWS. Backend authority is unchanged — the
 * 2026-06 Sub-Admin parity rule still governs API access (grants are a UI
 * visibility signal, not a backend boundary; money/credential routes stay
 * `requireRole('company_admin')`).
 */
function DashboardRoute() {
  const role = useAuthStore((s) => s.user?.role)
  const authStatus = useAuthStore((s) => s.status)
  // Hold until auth settles so a Sub Admin isn't flashed the full panel first.
  if (authStatus === 'unknown') return null
  return role === 'sub_admin' ? <SubAdminDashboard /> : <DashboardPage />
}

function MaintenanceGate({ children }) {
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const authStatus = useAuthStore((s) => s.status)
  const status = useMaintenanceStore((s) => s.status)
  const state = useMaintenanceStore((s) => s.state)

  // Belt-and-braces — also subscribe to the raw `flowdesk:
  // maintenance-hit` event the axios interceptor dispatches the
  // INSTANT a 503/maintenance lands. The store-driven redirect path
  // below (Zustand → re-render → `<Navigate>`) handles 99% of cases,
  // but a synchronous useEffect-driven push here means we never sit
  // on a stale page even if React Query's error render races the
  // store update. Idempotent — if we're already on `/maintenance`,
  // calling navigate(...) again is a no-op via `replace`.
  useEffect(() => {
    function onMaintenanceHit() {
      // Super Admins are exempt — they need to reach the toggles
      // page to turn maintenance back off.
      const scope = scopeForUser(user ?? null)
      if (!scope) return
      rememberMaintBypass(window.location.search)
      if (isMaintenanceExemptPath(window.location.pathname)) return
      navigate('/maintenance', { replace: true })
    }
    window.addEventListener('taskmagix:maintenance-hit', onMaintenanceHit)
    return () => {
      window.removeEventListener('taskmagix:maintenance-hit', onMaintenanceHit)
    }
  }, [navigate, user])

  // Wait for both the auth bootstrap AND the first maintenance fetch
  // to land. Without this the first paint flashes the maintenance
  // screen on signed-in users whose scope happens to be down — but
  // also flashes the LIVE app on users who should be locked out.
  if (authStatus === 'unknown' || status === 'unknown') {
    return children
  }

  const scope = scopeForUser(user ?? null)
  const down = scope ? state[scope]?.enabled : false

  // WEBSITE maintenance can take down the public auth flow; the Master
  // Admin gets in via `?admin=1`. Record the bypass on this render, then
  // gate everything that isn't an exempt path.
  rememberMaintBypass(location.search)
  const isAllowedPath = isMaintenanceExemptPath(location.pathname)

  if (down && !isAllowedPath) {
    return <Navigate to="/maintenance" replace />
  }
  return children
}

function AppInner() {
  const bootstrap = useAuthStore((s) => s.bootstrap)
  const user = useAuthStore((s) => s.user)
  const authStatus = useAuthStore((s) => s.status)
  const startMaintenance = useMaintenanceStore((s) => s.start)
  const stopMaintenance = useMaintenanceStore((s) => s.stop)
  const refreshMaintenance = useMaintenanceStore((s) => s.refresh)
  useEffect(() => {
    bootstrap()
  }, [bootstrap])

  useEffect(() => {
    /**
     * Re-check the session when the page is restored from the back/forward
     * cache.
     *
     * A bfcache restore does NOT re-run the app: the DOM and the JavaScript
     * heap come back frozen exactly as they were. So after signing out, a
     * single press of Back brought the previous page back fully rendered —
     * member names, email addresses and all — and nothing ever re-checked,
     * so it simply stayed there (QA-134). On a shared machine that hands the
     * next person the last person's data.
     *
     * `pageshow` with `persisted: true` is the only signal a restore gives.
     * Re-bootstrapping from there makes the store authoritative again, and
     * `ProtectedRoute` then redirects to sign-in when the session is gone.
     * A live session simply re-validates and the user notices nothing.
     */
    const onPageShow = (event) => {
      if (event.persisted) bootstrap()
    }
    window.addEventListener('pageshow', onPageShow)
    return () => window.removeEventListener('pageshow', onPageShow)
  }, [bootstrap])

  useEffect(() => {
    // A flash toast queued just before a hard navigation (e.g. entering /
    // exiting impersonation, which reload the page) survives the reload and
    // fires here once the destination has booted — a plain toast would have
    // been wiped by the reload before the user ever saw it.
    const flash = consumeFlashToast()
    if (flash) {
      if (flash.type === 'error') toast.error(flash.message)
      else toast.success(flash.message)
    }
  }, [])

  useEffect(() => {
    // Fire a fetch immediately on mount so unauthenticated visitors
    // see the maintenance screen on the first paint, then keep the
    // poll + socket listener alive for subsequent updates.
    void refreshMaintenance()
    startMaintenance()
    return () => stopMaintenance()
  }, [refreshMaintenance, startMaintenance, stopMaintenance])

  useEffect(() => {
    // Cross-device preferences (favourite Spaces / Lists + hidden Spaces).
    // Hydrate from the server once the signed-in user id is known, so the
    // same account shows the same pins on every device. Re-hydrate when the
    // user returns to the tab (near-immediate cross-device pickup) — but skip
    // if a local change is still pending so we never clobber an unsynced edit.
    // On sign-out, clear the sync so the next account can't push these.
    // (The same response also hydrates the display & accessibility
    // preferences — see display-prefs.js and the effect below.)
    if (!user?.id) {
      resetPreferencesSync()
      return
    }
    const uid = user.id
    void hydratePreferences(uid)
    const onFocus = () => {
      if (!hasPendingPreferenceSync()) void hydratePreferences(uid)
    }
    window.addEventListener('focus', onFocus)
    return () => window.removeEventListener('focus', onFocus)
  }, [user?.id])

  useEffect(() => {
    // Signed out (or no session): forget the display & accessibility
    // preferences so the next person on this computer starts from the
    // defaults, never from the previous account. Keyed on the settled auth
    // status — NOT on `user` being null — so the brief null before a stored
    // session restores doesn't flash the defaults over the user's theme.
    if (authStatus === 'unauthenticated') resetDisplayPrefsForSignOut()
  }, [authStatus])

  const homeTarget = user ? landingFor(user.role) : '/sign-in'

  return (
    <MaintenanceGate>
    <PlanGate>
    <ImpersonationBanner />
    <VisitorTracker />
    {/* Page Performance (Phase 1): times every page open in the background
     *  and posts the reports. Path only; renders nothing. */}
    <PagePerfTracker />
    <Routes>
      {/* While the initial /auth/me is still resolving — notably right after an
          org-switch or impersonation-exit hard-reload lands on '/' — `user` is
          momentarily null even though the session is valid. Redirecting to
          /sign-in here flashed the login page mid-switch. Hold on the boot
          blank (the app's 'unknown' convention, same as the protected-route
          gates) until bootstrap resolves, then land on the right home. */}
      <Route
        path="/"
        element={
          authStatus === 'unknown' ? null : <Navigate to={homeTarget} replace />
        }
      />
      <Route path="/sign-in" element={<SignInPage />} />
      <Route path="/sign-up" element={<SignUpPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Join Company (Phase 2) — public accept page for the single-use
       *  invitation link emailed to a new/existing member. */}
      <Route path="/join" element={<JoinPage />} />
      {/* Maintenance-mode landing. Reachable to anyone — the page
       *  itself decides whether to keep the user here or redirect
       *  them back when their scope is no longer in maintenance. */}
      <Route path="/maintenance" element={<MaintenanceScreen />} />

      {/* Company Admin + Sub Admin — flat URLs matching sidebar labels.
       *  Both roles share the same Admin panel; sidebar visibility and
       *  per-page gating handle what each individual Sub Admin can
       *  actually do via `hasSubAdminOperation()`. */}
      <Route element={<ProtectedRoute allowRoles={['company_admin', 'sub_admin']} />}>
        <Route element={<AdminLayout />}>
          {/* /dashboard — one URL, role-dispatched by `DashboardRoute`:
           *  Company Admins get the full `DashboardPage`; Sub Admins get the
           *  grant-aware `SubAdminDashboard` (only their delegated sections).
           *  Deep links + the role-based landing helper (`landingFor`) stay
           *  on `/dashboard` for both and don't need to branch. */}
          <Route
            path="/dashboard"
            element={<DashboardRoute />}
          />
          <Route path="/workspaces" element={<WorkspacesPage />} />
          <Route path="/members" element={<MembersPage />} />
          {/* `/team` was the standalone task-based directory view of
           *  the same roster. Folded into `/members?view=grid` on
           *  2026-06-08 — Members now offers a List ⇄ Grid view
           *  toggle. This redirect keeps every old bookmark, sidebar
           *  click, and dashboard deep-link working. */}
          <Route
            path="/team"
            element={<Navigate to="/members?view=grid" replace />}
          />
          <Route path="/departments" element={<DepartmentsPage />} />
          <Route path="/work-roles" element={<WorkRolesPage />} />
          {/* /custom-fields was removed from the Company Admin panel — see
           *  the note in AdminLayout.jsx. The page component is kept (like
           *  LabelsPage) so re-enabling it is just this route + the nav row. */}
          {/* Legacy URL from the earlier draft of this module — kept as
           *  a redirect so any bookmark of /roles still lands on the
           *  renamed page rather than 404. */}
          <Route
            path="/roles"
            element={<Navigate to="/work-roles" replace />}
          />
          {/* External Collaborators — plan-gated (off by default) via the
           *  `external_collaboration` flag; the backend route is gated the
           *  same way, so this only mirrors that block as UX. */}
          <Route
            element={
              <FeatureProtectedRoute
                feature="external_collaboration"
                title="External Collaborators"
              />
            }
          >
            <Route
              path="/external-collaborators"
              element={<ExternalCollaboratorsPage />}
            />
          </Route>
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/preferences" element={<PreferencesPage />} />
          {/* Task Monitor. Canonical URL is `/task-monitor` to match
           *  the sidebar label and every existing link (DashboardPage
           *  card, admin legacy redirect). The old `/tasks` URL keeps
           *  working via the redirect below. */}
          <Route path="/task-monitor" element={<TaskMonitorPage />} />
          {/* Task Verification board retired — its checker/verification view is
           *  now a filter on Task Monitor. Old URLs/bookmarks redirect there so
           *  they don't 404. */}
          <Route
            path="/task-verification"
            element={<Navigate to="/task-monitor" replace />}
          />
          <Route
            path="/accountability"
            element={<Navigate to="/task-monitor" replace />}
          />
          <Route
            path="/tasks"
            element={<Navigate to="/task-monitor" replace />}
          />
          {/* Work Reports + Reminders & Schedule. Canonical URLs match
           *  the sidebar labels (`/work-reports`, `/reminders-schedule`);
           *  the older short URLs (incl. the former `/report-schedule`)
           *  redirect for back-compat. */}
          <Route path="/work-reports" element={<WorkReportsPage />} />
          <Route
            path="/reports"
            element={<Navigate to="/work-reports" replace />}
          />
          <Route path="/reminders-schedule" element={<ReportSchedulePage />} />
          <Route
            path="/report-schedule"
            element={<Navigate to="/reminders-schedule" replace />}
          />
          <Route
            path="/schedule"
            element={<Navigate to="/reminders-schedule" replace />}
          />
          {/* Usage Report — a dedicated Company Admin page that owns the
           *  Report Recipients list (who receives the per-company Usage
           *  Report). Sits beside Report Schedule in the Configuration group. */}
          <Route path="/usage-report" element={<CompanyUsageReportPage />} />
          {/* Company Admin SOP integration. Canonical URL is
           *  `/sop-integration` to match the Super Admin convention
           *  (`/super-admin/sop-integration`) and the existing
           *  sidebar / SopsPage links. The old `/integrations` URL
           *  redirects below for back-compat. */}
          <Route
            element={
              <FeatureProtectedRoute
                feature="sop_integration"
                title="SOP Integration"
              />
            }
          >
            {/* Tenant-owner-only (in CA_ONLY_FOR_SUB_ADMIN) — guard the URL,
             *  not just the menu, in addition to the feature flag above. */}
            <Route
              path="/sop-integration"
              element={
                <CompanyAdminRoute>
                  <IntegrationsPage />
                </CompanyAdminRoute>
              }
            />
          </Route>
          <Route
            path="/integrations"
            element={<Navigate to="/sop-integration" replace />}
          />
          {/* Telegram bot — tenant-owner-only (guard the URL, not just the
           *  menu). No plan-feature gate; the bot's own enabled flag + the
           *  company-admin routes control exposure. */}
          <Route
            path="/telegram-integration"
            element={
              <CompanyAdminRoute>
                <TelegramPage />
              </CompanyAdminRoute>
            }
          />
          {/* [Servexa] Support Tickets (spec A6) — the ACTIVE support flow,
           *  submitted to Servexa. Canonical URL `/support-tickets` (kept so
           *  existing deep-links + the sidebar label stay stable); `/support`
           *  redirects for back-compat. The `:ticketId` variant lets a status
           *  webhook's notification deep-link straight onto a ticket. Replaces
           *  the retired, orphaned tenant SupportPage. */}
          <Route path="/support-tickets" element={<ServexaSupportPage />} />
          <Route path="/support-tickets/:ticketId" element={<ServexaSupportPage />} />
          <Route
            path="/support"
            element={<Navigate to="/support-tickets" replace />}
          />
          {/* [Servexa] Feature Suggestions (spec A7) — a SEPARATE flow from
           *  Support, never merged. `:featureId` deep-links from a webhook
           *  notification. */}
          <Route path="/feature-suggestions" element={<ServexaFeaturesPage />} />
          <Route
            path="/feature-suggestions/:featureId"
            element={<ServexaFeaturesPage />}
          />
          {/* Billing is tenant-owner-only — guard the URL, not just the menu. */}
          <Route
            path="/billing"
            element={
              <CompanyAdminRoute>
                <BillingPage />
              </CompanyAdminRoute>
            }
          />
          {/* Refer & Earn — available to every company; the programme's
           *  active state comes from the Super Admin config, surfaced in
           *  the page (no per-plan gate). */}
          <Route path="/referrals" element={<ReferralsPage />} />
          <Route
            element={
              <FeatureProtectedRoute feature="api_access" title="AI Provider Keys" />
            }
          >
            {/* API Keys is tenant-owner-only too (same caOnly rule) — guard
             *  the URL in addition to the feature flag above. */}
            <Route
              path="/api-keys"
              element={
                <CompanyAdminRoute>
                  <ApiKeysPage />
                </CompanyAdminRoute>
              }
            />
          </Route>
          <Route
            element={
              <FeatureProtectedRoute feature="workflows" title="Workflows" />
            }
          >
            <Route path="/workflows" element={<WorkflowsPage />} />
          </Route>
          <Route
            element={
              <FeatureProtectedRoute
                feature="gamification"
                title="Gamification"
              />
            }
          >
            <Route
              path="/gamification-settings"
              element={<GamificationSettingsPage />}
            />
            {/* Company Admins get the org leaderboard in their own panel.
             *  The employee copy lives at /app/leaderboard (user-only);
             *  this root-level route renders it inside AdminLayout so a
             *  Company Admin can see the same ranking they configure. */}
            <Route path="/leaderboard" element={<LeaderboardPage />} />
          </Route>
          <Route
            element={
              <FeatureProtectedRoute
                feature="sop_integration"
                title="SOPs Library"
              />
            }
          >
            <Route path="/sops-library" element={<SopsPage />} />
          </Route>
          {/* Legacy `/sops` → `/sops-library`. */}
          <Route
            path="/sops"
            element={<Navigate to="/sops-library" replace />}
          />
          <Route path="/audit-log" element={<AuditLogPage />} />
          <Route path="/login-activity" element={<LoginActivityPage />} />
          {/* Email Status Logs — tenant-scoped feed of outbound email
           *  with status + retry. Sidebar gates on `audit_log.view`
           *  (same op as Audit Log + Login Activity); the page itself
           *  is reachable directly. Backend filters to req.user.tenantId. */}
          <Route path="/email-logs" element={<AdminEmailLogsPage />} />
          <Route path="/files-links" element={<FilesPage />} />
          {/* Legacy `/files` → `/files-links` so the URL matches the
           *  sidebar label "Files & Links". */}
          <Route
            path="/files"
            element={<Navigate to="/files-links" replace />}
          />
          <Route
            element={
              <FeatureProtectedRoute feature="ai_usage" title="AI Usage" />
            }
          >
            <Route path="/ai-usage" element={<AiUsagePage />} />
          </Route>
          <Route path="/performance" element={<PerformancePage />} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
        </Route>
      </Route>

      {/* Shared Spaces UX — same /app/* URLs, role-aware chrome.
       *  RoleAwareLayout dispatches: company_admin / sub_admin get
       *  AdminLayout (with its own Spaces tree sidebar); user gets
       *  UserLayout. This route group MUST come before the user-only
       *  group below so React Router matches the more specific deep
       *  child first. The four pages listed here used to live inside
       *  UserLayout's children — they were lifted up so admins can
       *  reach them without being shoved into the user nav rail.
       *
       *  Backend permissions: `findWorkspaceMembership(actor, workspaceId)`
       *  short-circuits for actors holding `workspaces.manage`, so
       *  Company Admins and granted Sub Admins can read / write every
       *  space in the tenant without an explicit WorkspaceMember row. */}
      <Route element={<ProtectedRoute allowRoles={['user', 'company_admin', 'sub_admin']} />}>
        <Route path="/app" element={<RoleAwareLayout />}>
          <Route path="spaces/:spaceId" element={<SpacePage />} />
          <Route path="spaces/:spaceId/lists/:listId" element={<SpacePage />} />
          <Route path="board/:listId" element={<BoardPage />} />
          <Route path="documents/:id" element={<DocumentPage />} />
          <Route path="calendar" element={<CalendarPage />} />
          {/* Our Products — the shared hosted products feed
           *  (themasterdashboard.com), embedded in an iframe. Shared by every
           *  customer role (user / company_admin / sub_admin); RoleAwareLayout
           *  gives each its own chrome. */}
          <Route path="our-products" element={<OurProductsPage />} />
          {/* User Manual — role-based guide. RoleAwareLayout gives each
           *  customer role (user / company_admin / sub_admin) its own chrome;
           *  UserManualPage picks the manual from the signed-in user's role. */}
          <Route path="user-manual" element={<UserManualPage />} />
          {/* Goals — unified Weekly + Monthly goals, shared by every customer
           *  role (user / company_admin / sub_admin) with its own chrome.
           *  Plan-gated (fail-open) via the `weekly_goals` feature flag;
           *  `/app/weekly-goals` redirects to the Weekly tab for old bookmarks. */}
          <Route
            element={<FeatureProtectedRoute feature="weekly_goals" title="Goals" />}
          >
            <Route path="goals" element={<GoalsPage />} />
            <Route path="goals/:id" element={<GoalDetailPage />} />
            <Route path="weekly-goals" element={<Navigate to="/app/goals?tab=weekly" replace />} />
          </Route>
          {/* Personal Reports — Daily / Weekly / Monthly. Shared by EVERY
           *  customer role (moved out of the user-only tree below so a Company
           *  Admin / Sub Admin can file their own reports too — the submit
           *  endpoint is auth-only, and the report data feeds the Goals
           *  "Actual Work" Hours/Meetings/Days). RoleAwareLayout gives each
           *  role its own chrome. Bare `/app/reports` → the Daily tab. */}
          <Route
            path="reports"
            element={<Navigate to="/app/reports/daily" replace />}
          />
          <Route path="reports/daily" element={<DailyReportPage />} />
          <Route path="reports/weekly" element={<WeeklyReportPage />} />
          <Route path="reports/monthly" element={<MonthlyReportPage />} />
          {/* Report Settings — Manager / Company Admin / Sub Admin configure the
           *  report Task Type + Item Status catalogues. Lives in the SHARED
           *  RoleAwareLayout group (not the user-only tree) so admins reach it
           *  under AdminLayout too. GET is read-open (form pre-fills defaults);
           *  the PUT enforces workspace/department reports access server-side. */}
          <Route path="report-settings" element={<ReportSettingsPage />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute allowRoles={['user']} />}>
        <Route path="/app" element={<UserLayout />}>
          <Route index element={<UserHomePage />} />
          <Route path="focus-board" element={<MyDayPage />} />
          {/* Legacy URL — anyone with `/app/my-day` bookmarked or in
           *  their browser history lands on the renamed Focus Board
           *  instead of a 404. Safe to keep indefinitely; the
           *  redirect costs nothing. */}
          <Route
            path="my-day"
            element={<Navigate to="/app/focus-board" replace />}
          />
          {/* Legacy /app/achievements → /app/performance after the
           *  Achievements + Performance pages merged into one
           *  role-adaptive page. The personal trophy case now sits
           *  above the team analytics on Performance. */}
          <Route
            path="achievements"
            element={<Navigate to="/app/performance" replace />}
          />
          <Route
            element={
              <FeatureProtectedRoute
                feature="sop_integration"
                title="SOPs Library"
              />
            }
          >
            <Route path="sops-library" element={<SopsPage />} />
          </Route>
          {/* Legacy `/app/sops` → `/app/sops-library`. */}
          <Route path="sops" element={<Navigate to="/app/sops-library" replace />} />
          <Route path="replies" element={<RepliesPage />} />
          <Route
            path="assigned-comments"
            element={<AssignedCommentsPage />}
          />
          {/* My Tasks page was removed; /app/my-tasks now redirects to
           *  the Focus Board so existing links / bookmarks / deep links
           *  still resolve instead of 404-ing. */}
          <Route
            path="my-tasks"
            element={<Navigate to="/app/focus-board" replace />}
          />
          <Route path="team-tasks" element={<TeamTasksPage />} />
          {/* Personal Reports (bare `/app/reports` redirect + daily / weekly /
           *  monthly) MOVED to the shared RoleAwareLayout tree above so every
           *  customer role — incl. Company Admin / Sub Admin — can file them.
           *  For the 'user' role RoleAwareLayout renders this same UserLayout,
           *  so the employee experience is unchanged. `reports/team` stays here
           *  (Manager team view; admins read team reports via /work-reports). */}
          <Route path="reports/team" element={<TeamReportsPage />} />
          {/* `report-settings` MOVED to the shared RoleAwareLayout group above so
           *  Company Admin / Sub Admin can reach it too (it was user-only here,
           *  which hid it from the admin chrome). UserLayout still links it. */}
          {/* `<Route path="timesheet">` removed 2026-05-27 — Time
           *  Tracking module retired. Direct visits to /app/timesheet
           *  fall through to the NotFound catch-all at the bottom of
           *  this Routes tree, which is the desired UX. */}
          <Route
            element={
              <FeatureProtectedRoute
                feature="gamification"
                title="Leaderboard"
              />
            }
          >
            <Route path="leaderboard" element={<LeaderboardPage />} />
          </Route>
          <Route path="performance" element={<PerformancePage />} />
          <Route
            path="team-overview"
            element={<WorkspaceOverviewPage />}
          />
          {/* Team Task Analytics + Employee Day-Audit (Supervision). The page
           *  gates itself: the API returns 403 for a Self-only viewer and the
           *  page shows a "not available for your role" panel. */}
          <Route path="team-analytics" element={<TeamAnalyticsPage />} />
          {/* Legacy `/app/workspace-overview` → `/app/team-overview`
           *  so the URL matches the sidebar label "Team Overview". */}
          <Route
            path="workspace-overview"
            element={<Navigate to="/app/team-overview" replace />}
          />
          {/* Notifications — historical /messages and /inbox redirect here. */}
          <Route path="notifications" element={<NotificationsPage />} />
          <Route
            path="messages"
            element={<Navigate to="/app/notifications" replace />}
          />
          <Route
            path="inbox"
            element={<Navigate to="/app/notifications" replace />}
          />
          <Route path="profile" element={<ProfilePage padded />} />
          {/* "Your timezone" (a personal zone) is a member-panel setting only;
           *  the admin panels work in the company timezone. */}
          <Route path="preferences" element={<PreferencesPage padded personalTimezone />} />
          <Route path="files-links" element={<FilesPage />} />
          <Route path="files" element={<Navigate to="/app/files-links" replace />} />
        </Route>
      </Route>

      <Route element={<ProtectedRoute allowRoles={['super_admin']} />}>
        <Route path="/super-admin" element={<SuperAdminLayout />}>
          <Route
            index
            element={<Navigate to="/super-admin/dashboard" replace />}
          />
          <Route path="dashboard" element={<SuperAdminDashboard />} />
          <Route path="analytics" element={<AnalyticsPage />} />
          <Route
            path="profile"
            element={
              <ProfilePage
                padded
                homePath="/super-admin/dashboard"
                homeLabel="Dashboard"
              />
            }
          />
          <Route
            path="preferences"
            element={
              <PreferencesPage
                padded
                homePath="/super-admin/dashboard"
                homeLabel="Dashboard"
              />
            }
          />
          <Route path="companies" element={<TenantsPage />} />
          <Route path="companies/:id" element={<TenantDetailPage />} />
          {/* Legacy /tenants → /companies redirects. */}
          <Route
            path="tenants"
            element={<Navigate to="/super-admin/companies" replace />}
          />
          <Route
            path="tenants/:id"
            element={<Navigate to="/super-admin/companies" replace />}
          />
          <Route path="all-users" element={<AllUsersPage />} />
          <Route
            path="users"
            element={<Navigate to="/super-admin/all-users" replace />}
          />
          <Route path="plans" element={<PlansPage />} />
          <Route path="ai-usage" element={<PlatformAiUsagePage />} />
          <Route path="storage" element={<PlatformStoragePage />} />
          <Route path="audit-logs" element={<AuditLogsPage />} />
          <Route path="login-activity" element={<PlatformLoginActivityPage />} />
          <Route path="visitor-logs" element={<VisitorLogsPage />} />
          {/* Page Performance — Master Admin only; cross-tenant reads by design. */}
          <Route path="page-performance" element={<PagePerformancePage />} />
          <Route path="coupons" element={<CouponsPage />} />
          <Route path="referrals" element={<PlatformReferralsPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="data-platforms" element={<SoftwareWatchPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          {/* Operator's view of the shared hosted products feed — same iframe
           *  page as the customer roles, per the integration guide's rule that
           *  every human-facing role can reach "Our Products". */}
          <Route path="our-products" element={<OurProductsPage />} />
          {/* User Manual — the platform operator's guide. */}
          <Route path="user-manual" element={<UserManualPage />} />
          <Route path="support-tickets" element={<SupportTicketsPage />} />
          <Route
            path="support-tickets/:ticketId"
            element={<SupportTicketsPage />}
          />
          <Route path="settings" element={<PlatformSettingsPage />} />
          <Route path="payments" element={<PaymentsPage />} />
          <Route path="email-templates" element={<EmailTemplatesPage />} />
          {/* Dedicated editor page replaces the old modal. Both routes
           *  resolve to the same component; `:id/edit` hydrates the
           *  form from the existing row, `/new` starts blank. */}
          <Route
            path="email-templates/new"
            element={<EmailTemplateEditorPage />}
          />
          <Route
            path="email-templates/:id/edit"
            element={<EmailTemplateEditorPage />}
          />
          {/* Email Status Logs — the per-send delivery feed that used
           *  to live as a tab inside Email Templates. Reads the
           *  /super-admin/email-logs surface and renders the shared
           *  <SentEmailsView />, the same body the Company Admin
           *  "Email Status Logs" page uses against its own tenant. */}
          <Route path="email-logs" element={<SentEmailsPage />} />
          {/* Legacy /super-admin/sent-emails → renamed to email-logs. */}
          <Route
            path="sent-emails"
            element={<Navigate to="/super-admin/email-logs" replace />}
          />
          {/* Legacy /email-centre → the real Email Templates module. */}
          <Route
            path="email-centre"
            element={<Navigate to="/super-admin/email-templates" replace />}
          />
          <Route path="sop-integration" element={<SopIntegrationPage />} />
          {/* [Servexa] Super Admin Integration panel (spec A9/A20). Its own
           *  path, guarded by the super_admin ProtectedRoute above — never a
           *  generic settings screen. */}
          <Route
            path="servexa-integration"
            element={<ServexaIntegrationPage />}
          />
          {/* [Telegram bot] The PLATFORM's single bot connection — Master Admin
           *  only, guarded by the super_admin ProtectedRoute above. Companies
           *  configure nothing; they only connect individual members. */}
          <Route
            path="telegram-integration"
            element={<TelegramIntegrationPage />}
          />
          {/* Legacy `/super-admin/integrations` → `/super-admin/sop-integration`. */}
          <Route
            path="integrations"
            element={<Navigate to="/super-admin/sop-integration" replace />}
          />
          {/* Maintenance + IP Access moved into the Settings page (their
           *  own sections). Redirect old bookmarks + the super-admin
           *  escape-hatch links there. */}
          <Route
            path="maintenance"
            element={<Navigate to="/super-admin/settings" replace />}
          />
          <Route
            path="ip-access"
            element={<Navigate to="/super-admin/settings" replace />}
          />
          <Route path="usage-report" element={<UsageReportPage />} />
          {/* Google SSO moved into Settings tabs — redirect old links. */}
          <Route
            path="google-sso"
            element={<Navigate to="/super-admin/settings" replace />}
          />
          <Route path="notifications" element={<NotificationsPage />} />
        </Route>
      </Route>

      {/* Legacy /admin/* → redirect to the new flat paths for anyone with old bookmarks */}
      <Route path="/admin" element={<Navigate to="/dashboard" replace />} />
      <Route path="/admin/members" element={<Navigate to="/members" replace />} />
      <Route path="/admin/tasks" element={<Navigate to="/task-monitor" replace />} />
      <Route path="/admin/reports" element={<Navigate to="/work-reports" replace />} />
      <Route path="/admin/schedule" element={<Navigate to="/reminders-schedule" replace />} />
      <Route path="/admin/profile" element={<Navigate to="/profile" replace />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
    </PlanGate>
    </MaintenanceGate>
  )
}

/** Auth pages (sign-in / sign-up / forgot / reset) render without the
 *  64px app header, so the global Toaster's 76px top offset would drop
 *  toasts onto the sign-in card's "Welcome back" header. While on one of
 *  those routes we set `--fd-toast-top: 24px` on the document root — the
 *  Toaster reads it via `offset={{ top: 'var(--fd-toast-top, 76px)' }}`
 *  (custom properties inherit into Sonner's body-portalled stack) — which
 *  pulls toasts into the true top-right corner. Cleared elsewhere so the
 *  offset falls back to 76px. Renders nothing. */
const AUTH_TOAST_PATHS = [
  '/sign-in',
  '/sign-up',
  '/forgot-password',
  '/reset-password',
]

function ToasterOffsetSync() {
  const { pathname } = useLocation()
  useEffect(() => {
    const isAuthPage = AUTH_TOAST_PATHS.some((p) => pathname.startsWith(p))
    const root = document.documentElement
    if (isAuthPage) root.style.setProperty('--fd-toast-top', '24px')
    else root.style.removeProperty('--fd-toast-top')
    return () => root.style.removeProperty('--fd-toast-top')
  }, [pathname])
  return null
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      {/* Subscribes once to the backend's `data:changed` socket event
       *  and invalidates React Query keys so every open tab in the
       *  tenant stays in sync without manual refresh. Renders nothing. */}
      <RealtimeInvalidator />
      {/* One styled hover-tooltip chip for the whole app — intercepts every
       *  element's native `title` so icon buttons, badges, pills, etc. all
       *  read like the Avatar / assignee chip instead of the OS tooltip. */}
      <GlobalTooltip />
      <BrowserRouter>
        {/* Keeps the browser tab title in sync with the Super Admin
         *  Settings → Platform Name (live on save, no refresh) AND with the
         *  current route. Must sit INSIDE BrowserRouter — it reads
         *  useLocation() to name the page. */}
        <PlatformTitle />
        <Toaster
          position="top-right"
          /* Top offset clears the fixed 64px app header (+12px gap) so
           *  toasts stack in the top-right corner below it. Auth pages
           *  (sign-in / sign-up / forgot / reset) have NO app header, so
           *  `ToasterOffsetSync` sets `--fd-toast-top: 24px` there to pull
           *  the stack into the true corner instead of onto the card
           *  header. Falls back to 76px everywhere else. Right offset
           *  keeps them snug to the edge. */
          offset={{ top: 'var(--fd-toast-top, 76px)', right: '24px' }}
          duration={4000}
          theme="light"
          closeButton
          expand
          gap={4}
          /* 3 (Sonner's own default) rather than 5. Five stacked toasts
           *  cover a large slice of the top-right, which is exactly where
           *  the Change Password card sits on My Profile — QA hit a stack
           *  tall enough to bury that card's first field and its show/hide
           *  control. Capping the stack bounds how much UI a burst of
           *  toasts can ever obscure. Repeat toasts for the SAME action no
           *  longer stack at all: those pass a stable `id`, so a second
           *  save replaces the first toast instead of adding to the pile. */
          visibleToasts={3}
          /* `richColors` is intentionally OFF — it tints the toast
           *  background AND the icon per type with Sonner's stock
           *  palette, which doesn't match the brand. Custom per-type
           *  styling is handled entirely in `index.css` via the
           *  `[data-sonner-toast][data-type="..."]` selectors plus
           *  the filled-circle icons we pass below. */
          /* Custom filled-circle icons — replaces Sonner's stock
           *  outlined glyphs (which read weak next to the colored
           *  background tint) with a solid circle + white glyph that
           *  reads at a glance, mirroring the design references the
           *  user shared. One JSX node per type; Sonner renders
           *  whatever node we hand it inside the `[data-icon]`
           *  container, so the sizing/centering is consistent with
           *  every other toast renderer in the app. */
          icons={{
            success: (
              <span
                className="fd-toast-icon-circle"
                style={{ background: 'var(--color-success)' }}
              >
                <Check size={14} strokeWidth={3.5} />
              </span>
            ),
            error: (
              <span
                className="fd-toast-icon-circle"
                style={{ background: 'var(--color-danger)' }}
              >
                <XIcon size={14} strokeWidth={3.5} />
              </span>
            ),
            warning: (
              <span
                className="fd-toast-icon-circle"
                style={{ background: 'var(--color-warning)' }}
              >
                <AlertTriangle size={13} strokeWidth={3} />
              </span>
            ),
            info: (
              <span
                className="fd-toast-icon-circle"
                style={{ background: 'var(--color-accent)' }}
              >
                <Info size={14} strokeWidth={3} />
              </span>
            ),
          }}
          toastOptions={{
            // `toast-progress` draws the bottom countdown bar (styles in
            // index.css). Its CSS animation runs for 4000ms to match the
            // Toaster `duration` above — keep the two in sync if the
            // duration changes. (Duration lives ONLY on the Toaster prop;
            // a per-toast `duration` here would silently override it.)
            // Font-size, tints, left border and per-type colored titles
            // also live in index.css under the toast section.
            className: 'toast-progress',
          }}
        />
        <ToasterOffsetSync />
        <AppInner />
        <SearchPalette />
      </BrowserRouter>
    </QueryClientProvider>
  )
}

export default App
